import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { esDni, esNie } from "@/lib/validacionEs";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

export function etiquetaConductor(c) {
  const n = (c?.nombre || "").trim();
  const d = (c?.dni || "").trim();
  if (n && d) return `${n} (${d})`;
  return n;
}

export default function ConductorSelect({ transportistaNif, transportistaRazon, transportistaCodigo, value, onChange, testid }) {
  const fid = React.useId();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [alta, setAlta] = useState(false);
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const [busy, setBusy] = useState(false);
  const nif = (transportistaNif || "").trim().toUpperCase();
  const sageCon = !!user?.sage_conductores && user?.sage_conectado === true;
  const [qSage, setQSage] = useState("");

  const reload = async () => {
    if (!nif) {
      setItems([]);
      return;
    }
    const params = { transportista_nif: nif || undefined, transportista_codigo: transportistaCodigo || undefined };
    const nom = (transportistaRazon || "").trim();
    if (sageCon && nom.length >= 2) params.buscar = nom;
    const { data } = await api.get("/api/v1/conductores", { params });
    setItems((data.items || []).filter((c) => c.activo !== false));
  };

  useEffect(() => {
    if (!nif) {
      setItems([]);
      setAlta(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = { transportista_nif: nif || undefined, transportista_codigo: transportistaCodigo || undefined };
        const nom = (transportistaRazon || "").trim();
        if (sageCon && nom.length >= 2) params.buscar = nom;
        const { data } = await api.get("/api/v1/conductores", { params });
        if (!cancelled) setItems((data.items || []).filter((c) => c.activo !== false));
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [nif, transportistaCodigo, transportistaRazon, sageCon]);

  useEffect(() => {
    if (!sageCon) return;
    const q = qSage.trim();
    if (q.length < 2) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/v1/conductores", { params: { buscar: q, page: 1, page_size: 50 } });
        if (cancelled) return;
        setItems((prev) => {
          const seen = new Set(prev.map((c) => String(c.id || c.dni || c.nombre)));
          const extra = (data.items || []).filter((c) => c.activo !== false && !seen.has(String(c.id || c.dni || c.nombre)));
          return extra.length ? prev.concat(extra) : prev;
        });
      } catch { /* busqueda Sage opcional */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [qSage, sageCon]);

  useEffect(() => {
    if (!value || !items.length) return;
    if (items.some((c) => etiquetaConductor(c) === value)) return;
    const want = value.replace(/\s*\([^)]*\)\s*$/, "").trim().toLowerCase();
    if (!want) return;
    const byName = items.find((c) => (c.nombre || "").trim().toLowerCase() === want);
    if (byName) onChange(etiquetaConductor(byName));
  }, [items, value, onChange]);

  const guardarAlta = async () => {
    const nom = nombre.trim();
    const id = dni.trim().toUpperCase();
    if (!nom) {
      toast.error("Indica el nombre del conductor");
      return;
    }
    if (!esDni(id) && !esNie(id)) {
      toast.error("DNI / NIE no v" + String.fromCharCode(0xe1) + "lido (letra de control)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/v1/conductores", {
        transportista_nif: nif,
        transportista_razon_social: transportistaRazon || "",
        nombre: nom,
        dni: id,
        activo: true,
      });
      await reload();
      onChange(etiquetaConductor(data));
      setNombre("");
      setDni("");
      setAlta(false);
      toast.success("Conductor a" + String.fromCharCode(0xf1) + "adido al maestro");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!nif) {
    return (
      <div>
        <Label htmlFor={fid} className="label-tag">Conductor</Label>
        <div id={fid} className="mt-1 text-xs text-slate-500 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
          Elige primero el transportista.
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={fid} className="label-tag">Conductor</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger id={fid} className="mt-1" data-testid={testid}>
          <SelectValue placeholder="Selecciona conductor" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Sin conductor</SelectItem>
          {value && !items.some((c) => etiquetaConductor(c) === value) ? (
            <SelectItem value={value}>{value}</SelectItem>
          ) : null}
          {items.map((c) => (
            <SelectItem key={c.id || c.dni || c.nombre} value={etiquetaConductor(c)}>{etiquetaConductor(c)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {sageCon ? (
        <Input
          className="mt-2"
          value={qSage}
          onChange={(e) => setQSage(e.target.value)}
          placeholder="Buscar conductor en Sage (2 letras)"
          aria-label="Buscar conductor en Sage"
        />
      ) : null}
      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2">
        {items.length === 0 ? <span>Este transportista no tiene conductores. </span> : null}
        <button type="button" className="text-blue-700 underline" onClick={() => setAlta((v) => !v)}>
          {alta ? "Cerrar alta" : "Alta r" + String.fromCharCode(0xe1) + "pida"}
        </button>
        <Link to="/conductores" className="text-blue-700 underline">Mantenimiento</Link>
      </div>
      {alta ? (
        <div className="mt-2 grid sm:grid-cols-[1fr_8rem_auto] gap-2">
          <Input
            value={nombre}
            maxLength={80}
            onChange={(ev) => setNombre(ev.target.value)}
            placeholder="Nombre y apellidos"
            aria-label="Nombre y apellidos"
            data-testid={testid ? `${testid}-alta-nombre` : undefined}
          />
          <Input
            className="font-mono uppercase"
            value={dni}
            maxLength={16}
            onChange={(ev) => setDni(ev.target.value)}
            placeholder="DNI / NIE"
            aria-label="DNI / NIE"
            data-testid={testid ? `${testid}-alta-dni` : undefined}
          />
          <Button type="button" size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={guardarAlta}>
            Guardar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
